// Pilot Categories Configuration
// Fetches from API (source of truth) with local fallback

const API = process.env.REACT_APP_BACKEND_URL;

export const DASHBOARD_TYPES = {
  EVENT: 'event',
  PROVIDER: 'provider',
  INSTRUCTOR: 'instructor'
};

// Local cache for fetched categories
let cachedCategories = null;
let cacheTimestamp = null;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Default fallback categories (used only if API fails)
const FALLBACK_CATEGORIES = {
  events: {
    id: 'events',
    displayName: 'Events',
    icon: 'PartyPopper',
    color: '#6366F1',
    bgColor: 'bg-violet-100',
    textColor: 'text-violet-600',
    dashboardType: 'event',
    status: 'live',
    quickActions: {
      enabled: true,
      actions: [
        { id: 'share_with_chef', title: 'Share with Chef', subtitle: 'Dietary summary', icon: 'Utensils', color: 'orange' },
        { id: 'share_with_venue', title: 'Share with Venue', subtitle: 'Accessibility needs', icon: 'Accessibility', color: 'blue' },
        { id: 'share_with_dj', title: 'Share with DJ', subtitle: 'Tap In playlist', icon: 'Music', color: 'purple' }
      ]
    },
    tapIn: { available: true, defaultEnabled: true },
    statCards: { total_attending: true, rsvps: true, checked_in: true, dietary_needs: true, accessibility: true },
    intake: { collect_dietary: true, collect_accessibility: true, collect_interests: true, collect_music_preferences: true, collect_vibe: true },
    cardLabel: 'ATTENDEE CARD',
    terminology: { attendee: 'Attendee', rsvp: 'RSVP', event: 'Event' }
  },
  instructor: {
    id: 'instructor',
    displayName: 'Instructor',
    icon: 'GraduationCap',
    color: '#3B82F6',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-600',
    dashboardType: 'event',
    status: 'live',
    quickActions: {
      enabled: true,
      actions: [
        { id: 'share_with_instructor', title: 'Share with Instructor', subtitle: 'Attendee summary', icon: 'GraduationCap', color: 'indigo' },
        { id: 'share_accessibility', title: 'Share Accessibility', subtitle: 'Accessibility needs', icon: 'Accessibility', color: 'blue' },
        { id: 'share_music', title: 'Share Music Preferences', subtitle: 'Attendee music picks', icon: 'Music', color: 'purple' }
      ]
    },
    tapIn: { available: true, defaultEnabled: true },
    statCards: { total_attending: true, rsvps: true, checked_in: true, dietary_needs: false, accessibility: true },
    intake: { collect_dietary: false, collect_accessibility: true, collect_interests: true, collect_music_preferences: true },
    cardLabel: 'PARTICIPANT CARD',
    terminology: { attendee: 'Participant', rsvp: 'Register', event: 'Workshop' }
  },
  makeup: {
    id: 'makeup',
    displayName: 'Makeup',
    icon: 'Sparkles',
    color: '#EC4899',
    bgColor: 'bg-pink-100',
    textColor: 'text-pink-600',
    dashboardType: 'provider',
    status: 'setup',
    quickActions: { enabled: false, actions: [] },
    tapIn: { available: true, defaultEnabled: false },
    statCards: { total_clients: true, bookings: true, service_history: true, tips: true },
    intake: { collect_skin_type: true, collect_skin_concerns: true, collect_allergies: true, collect_inspo_images: true },
    cardLabel: 'BEAUTY CLIENT CARD',
    terminology: { attendee: 'Client', rsvp: 'Book', event: 'Appointment' }
  },
  culinary: {
    id: 'culinary',
    displayName: 'Culinary',
    icon: 'ChefHat',
    color: '#F97316',
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-600',
    dashboardType: 'provider',
    status: 'setup',
    quickActions: { enabled: false, actions: [] },
    tapIn: { available: true, defaultEnabled: false },
    statCards: { total_clients: true, orders: true, dietary_needs: true, tips: true },
    intake: { collect_dietary: true, collect_allergies: true, collect_cuisine_preferences: true },
    cardLabel: 'GUEST CARD',
    terminology: { attendee: 'Guest', rsvp: 'Book', event: 'Experience' }
  },
  hair: {
    id: 'hair',
    displayName: 'Hair',
    icon: 'Scissors',
    color: '#F59E0B',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-600',
    dashboardType: 'provider',
    status: 'setup',
    quickActions: { enabled: false, actions: [] },
    tapIn: { available: true, defaultEnabled: false },
    statCards: { total_clients: true, bookings: true, tips: true },
    intake: { collect_hair_type: true, collect_hair_texture: true, collect_inspo_images: true },
    cardLabel: 'BEAUTY CLIENT CARD',
    terminology: { attendee: 'Client', rsvp: 'Book', event: 'Appointment' }
  },
  nails: {
    id: 'nails',
    displayName: 'Nails',
    icon: 'Palette',
    color: '#8B5CF6',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-600',
    dashboardType: 'provider',
    status: 'setup',
    quickActions: { enabled: false, actions: [] },
    tapIn: { available: true, defaultEnabled: false },
    statCards: { total_clients: true, bookings: true, tips: true },
    intake: { collect_nail_shape: true, collect_nail_length: true, collect_inspo_images: true },
    cardLabel: 'BEAUTY CLIENT CARD',
    terminology: { attendee: 'Client', rsvp: 'Book', event: 'Appointment' }
  },
  lash: {
    id: 'lash',
    displayName: 'Lashes',
    icon: 'Eye',
    color: '#7C3AED',
    bgColor: 'bg-violet-100',
    textColor: 'text-violet-600',
    dashboardType: 'provider',
    status: 'setup',
    quickActions: { enabled: false, actions: [] },
    tapIn: { available: true, defaultEnabled: false },
    statCards: { total_clients: true, bookings: true, tips: true },
    intake: { collect_lash_type: true, collect_eye_shape: true, collect_inspo_images: true },
    cardLabel: 'BEAUTY CLIENT CARD',
    terminology: { attendee: 'Client', rsvp: 'Book', event: 'Appointment' }
  },
  wellness: {
    id: 'wellness',
    displayName: 'Wellness',
    icon: 'Heart',
    color: '#10B981',
    bgColor: 'bg-emerald-100',
    textColor: 'text-emerald-600',
    dashboardType: 'provider',
    status: 'setup',
    quickActions: { enabled: false, actions: [] },
    tapIn: { available: true, defaultEnabled: false },
    statCards: { total_clients: true, bookings: true, tips: true },
    intake: { collect_health_goals: true, collect_dietary_preferences: true },
    cardLabel: 'WELLNESS CARD',
    terminology: { attendee: 'Client', rsvp: 'Book', event: 'Session' }
  },
  travel: {
    id: 'travel',
    displayName: 'Travel',
    icon: 'Plane',
    color: '#3B82F6',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-600',
    dashboardType: 'provider',
    status: 'coming_soon',
    quickActions: { enabled: false, actions: [] },
    tapIn: { available: true, defaultEnabled: false },
    statCards: { total_clients: true, trips_planned: true },
    intake: { collect_travel_preferences: true, collect_accommodation_preferences: true },
    cardLabel: 'TRAVELER CARD',
    terminology: { attendee: 'Traveler', rsvp: 'Book', event: 'Trip' }
  },
  dispensary: {
    id: 'dispensary',
    displayName: 'Dispensary',
    icon: 'Leaf',
    color: '#22C55E',
    bgColor: 'bg-green-100',
    textColor: 'text-green-600',
    dashboardType: 'provider',
    status: 'coming_soon',
    quickActions: { enabled: false, actions: [] },
    tapIn: { available: true, defaultEnabled: false },
    statCards: { total_members: true, visits: true },
    intake: { collect_consumption_method: true, age_verification_required: true },
    cardLabel: 'MEMBER CARD',
    terminology: { attendee: 'Member', rsvp: 'Join', event: 'Visit' }
  },
  style: {
    id: 'style',
    displayName: 'Style',
    alternateNames: ['style'],
    icon: 'ShoppingBag',
    color: '#6366F1',
    bgColor: 'bg-indigo-100',
    textColor: 'text-indigo-600',
    dashboardType: 'provider',
    status: 'coming_soon',
    quickActions: { enabled: false, actions: [] },
    tapIn: { available: true, defaultEnabled: false },
    statCards: { total_clients: true, appointments: true },
    intake: { collect_style_preferences: true, collect_sizes: true },
    cardLabel: 'SHOPPER CARD',
    terminology: { attendee: 'Shopper', rsvp: 'Book', event: 'Appointment' }
  }
};

// Async function to fetch categories from API
export async function fetchPilotCategories() {
  // Check cache
  if (cachedCategories && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_TTL)) {
    return cachedCategories;
  }
  
  try {
    const response = await fetch(`${API}/api/platform-defaults/public/pilot-categories`);
    if (response.ok) {
      const data = await response.json();
      cachedCategories = data.categories || FALLBACK_CATEGORIES;
      cacheTimestamp = Date.now();
      return cachedCategories;
    }
  } catch (error) {
    console.warn('Failed to fetch pilot categories from API, using fallback:', error);
  }
  
  return FALLBACK_CATEGORIES;
}

// Synchronous getter that uses cache or fallback
export function getPilotCategoriesSync() {
  return cachedCategories || FALLBACK_CATEGORIES;
}

// Export PILOT_CATEGORIES for backward compatibility (uses fallback initially, updated when fetched)
export let PILOT_CATEGORIES = FALLBACK_CATEGORIES;

// Initialize by fetching (call this early in app startup)
export async function initPilotCategories() {
  const categories = await fetchPilotCategories();
  PILOT_CATEGORIES = categories;
  return categories;
}

// Helper functions
export function getPilotCategory(categoryId) {
  const categories = getPilotCategoriesSync();
  const normalized = (categoryId || '').toLowerCase();
  
  // Direct match
  if (categories[normalized]) {
    return categories[normalized];
  }
  
  // Check alternate names
  for (const [key, config] of Object.entries(categories)) {
    if (key === normalized) return config;
    if (config.alternateNames?.includes(normalized)) return config;
  }
  
  return null;
}

export function isEventDashboard(categoryId) {
  const category = getPilotCategory(categoryId);
  return category?.dashboardType === 'event';
}

export function isProviderDashboard(categoryId) {
  const category = getPilotCategory(categoryId);
  return category?.dashboardType === 'provider';
}

export function hasQuickActions(categoryId) {
  const category = getPilotCategory(categoryId);
  return category?.quickActions?.enabled === true;
}

export function getDefaultTapInState(categoryId) {
  const category = getPilotCategory(categoryId);
  return category?.tapIn?.defaultEnabled || false;
}

export function getStatCardsConfig(categoryId) {
  const category = getPilotCategory(categoryId);
  return category?.statCards || {};
}

export function getIntakeConfig(categoryId) {
  const category = getPilotCategory(categoryId);
  return category?.intake || {};
}

export function getTerminology(categoryId) {
  const category = getPilotCategory(categoryId);
  return category?.terminology || { attendee: 'Attendee', rsvp: 'RSVP', event: 'Event' };
}

export function getCategoryColor(categoryId) {
  const category = getPilotCategory(categoryId);
  return category?.color || '#6366F1';
}

export function getCategoryCardLabel(categoryId) {
  const category = getPilotCategory(categoryId);
  return category?.cardLabel || 'PREFERENCE CARD';
}

// Get all categories by dashboard type
export function getCategoriesByDashboardType(type) {
  const categories = getPilotCategoriesSync();
  return Object.values(categories).filter(c => c.dashboardType === type);
}

export function getEventCategories() {
  return getCategoriesByDashboardType('event');
}

export function getProviderCategories() {
  return getCategoriesByDashboardType('provider');
}

// Export list of all category IDs
export const ALL_PILOT_CATEGORY_IDS = Object.keys(FALLBACK_CATEGORIES);
export const EVENT_CATEGORY_IDS = ['events', 'instructor'];
export const PROVIDER_CATEGORY_IDS = ['makeup', 'culinary', 'hair', 'nails', 'lashes', 'wellness', 'travel', 'dispensary', 'style'];
